import React from "react";
import dayjs from "dayjs";
import {
    Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel,
    Badge, Box, Flex, Icon, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdShare, MdTextSnippet, MdHistory } from "react-icons/md";
import SocialLinksDisplay from "./SocialLinksDisplay";
import SensitiveOcrText from "./SensitiveOcrText";
import { AuditRow } from "./DetailHeaderParts";

/**
 * A single collapsible section with an icon, title, and optional count badge.
 * @param {Object} props
 * @param {React.ElementType} props.icon - Section icon
 * @param {string} props.title - Section title
 * @param {number} [props.count] - Optional count badge (hidden when falsy)
 * @param {React.ReactNode} props.children - Panel content
 * @returns {React.ReactElement}
 */
function CollapsibleItem({ icon, title, count, children }) {
    const headerBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <AccordionItem border="none" mb="8px">
            <AccordionButton bg={headerBg} borderRadius="10px" _hover={{ bg: headerBg }} px="12px" py="10px">
                <Flex flex="1" align="center" gap="8px">
                    <Icon as={icon} w="18px" h="18px" color="brand.500" />
                    <Text fontSize="sm" fontWeight="600" color={titleColor}>{title}</Text>
                    {count > 0 && <Badge colorScheme="brand" borderRadius="full" px="6px">{count}</Badge>}
                </Flex>
                <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px="4px" pt="12px" pb="4px">{children}</AccordionPanel>
        </AccordionItem>
    );
}

/**
 * Read-only block showing the last admin message sent to the user, if any.
 * @param {Object} props
 * @param {string} [props.message] - Existing admin_message
 * @returns {React.ReactElement|null}
 */
function LastAdminMessage({ message }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const textColor = useColorModeValue("secondaryGray.900", "white");
    if (!message) return null;
    return (
        <Box mt="8px" p="10px" bg={bg} borderRadius="10px">
            <Text fontSize="xs" color={labelColor} mb="4px">Dernier message envoyé</Text>
            <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap">{message}</Text>
        </Box>
    );
}

/**
 * Tracking info panel: last modification date, admin review audit line, and the
 * last admin message sent to the user.
 * @param {Object} props
 * @param {Object} props.certification - Certification data
 * @returns {React.ReactElement}
 */
function TrackingInfo({ certification }) {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            {certification.modified_at && (
                <Text fontSize="xs" color={color} mb="4px">
                    Dernière modification le {dayjs(certification.modified_at).format("DD/MM/YYYY HH:mm")}
                </Text>
            )}
            <AuditRow certification={certification} />
            <LastAdminMessage message={certification.admin_message} />
        </Box>
    );
}

/**
 * Collapsible secondary sections of the detail drawer (social links, OCR text,
 * tracking). Kept collapsed by default to surface only what is necessary first.
 * @param {Object} props
 * @param {Object} props.certification - Certification data
 * @param {Array} props.socialLinks - Resolved social link objects
 * @returns {React.ReactElement}
 */
function DrawerCollapsibles({ certification, socialLinks }) {
    return (
        <Accordion allowToggle>
            <CollapsibleItem icon={MdShare} title="Réseaux sociaux" count={socialLinks.length}>
                <SocialLinksDisplay socialLinks={socialLinks} />
            </CollapsibleItem>
            <CollapsibleItem icon={MdTextSnippet} title="Texte extrait (OCR)">
                <SensitiveOcrText certId={certification.id} />
            </CollapsibleItem>
            <CollapsibleItem icon={MdHistory} title="Suivi de la demande">
                <TrackingInfo certification={certification} />
            </CollapsibleItem>
        </Accordion>
    );
}

export default DrawerCollapsibles;
